import { UniqueIdentifier } from '@dnd-kit/core';
import { SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { type Quantity } from '@luminovo/api-contracts';
import {
    assertPresent,
    displayCurrencySymbol,
    isEqual,
    isPresent,
    MonetaryValue,
    QuantityHelper,
    throwErrorUnlessProduction,
} from '@luminovo/commons';
import {
    colorSystem,
    Divider,
    Flexbox,
    MenuItem,
    SecondaryIconButton,
    TertiaryButton,
    TertiaryIconButton,
    Text,
    Tooltip,
} from '@luminovo/design-system';
import { formattingOptions, formatToIso8601Date, getLocale } from '@luminovo/frontend-utils';
import { ListItem, Menu } from '@mui/material';
import { shallow, useSelector } from '@tanstack/react-store';
import {
    EllipsisIcon,
    EyeIcon,
    EyeOffIcon,
    FileSpreadsheetIcon,
    GripVerticalIcon,
    SettingsIcon,
    UndoIcon,
} from 'lucide-react';
import React from 'react';
import type { Row } from 'write-excel-file/browser';

import { stateSlicesEqual } from '../tanstackPrimitives';
import { Column, RowData, Table } from '../tanstackTypes';
import { getFilterConfig } from '../type';
import { resetTable } from '../utils';
import { ColumnConfigDialog } from './ColumnConfigDialog';
import { getManageableItems, isItemVisible } from './manageableItems';
import { MultiContainerDndContext } from './MultiContainerDndContext';

function ColumnManager<TData extends RowData>({ table }: { table: Table<TData> }) {
    const columnOrder = useSelector(table.store, (state) => state.columnOrder);
    const manageableItems = React.useMemo(() => getManageableItems(table), [table]);
    const leafColumns = table.getAllLeafColumns().filter((col) => col.getCanHide());
    const hasAllColumnVisible = manageableItems.every((item) => isItemVisible(item, table));
    const visibleCount = manageableItems.filter((item) => isItemVisible(item, table)).length;
    const hasOnlyOneItemVisible = visibleCount === 1;
    const { enableColumnHiding } = table.options.meta || {};

    const shouldShowHidenColumnManager = manageableItems.length < 12;

    const getItems = React.useCallback(() => {
        const leafOrder = columnOrder.length > 0 ? columnOrder : leafColumns.map(({ id }) => id);

        const seenItems = new Set<string>();
        const orderedItemIds: string[] = [];
        for (const leafId of leafOrder) {
            const item = manageableItems.find((m) => m.leafIds.includes(leafId));
            if (item && !seenItems.has(item.id)) {
                seenItems.add(item.id);
                orderedItemIds.push(item.id);
            }
        }
        for (const item of manageableItems) {
            if (!seenItems.has(item.id)) {
                orderedItemIds.push(item.id);
            }
        }

        const visible = orderedItemIds.filter((itemId) => {
            const item = manageableItems.find((m) => m.id === itemId);
            return item ? isItemVisible(item, table) : false;
        });
        const hidden = orderedItemIds.filter((itemId) => {
            const item = manageableItems.find((m) => m.id === itemId);
            return item ? !isItemVisible(item, table) : false;
        });

        return { visible, hidden };
    }, [columnOrder, leafColumns, manageableItems, table]);

    const expandItemsToLeafOrder = React.useCallback(
        (itemIds: string[]): string[] => {
            const leafOrder: string[] = [];
            for (const itemId of itemIds) {
                const item = manageableItems.find((m) => m.id === itemId);
                if (item) {
                    leafOrder.push(...item.leafIds);
                }
            }
            return leafOrder;
        },
        [manageableItems],
    );

    const updateItems = React.useCallback(
        (
            fn: (
                prev: Record<'visible' | 'hidden', UniqueIdentifier[]>,
            ) => Record<'visible' | 'hidden', UniqueIdentifier[]>,
        ) => {
            const newItems = fn(getItems());

            const newLeafOrder = expandItemsToLeafOrder([
                ...(newItems.visible as string[]),
                ...(newItems.hidden as string[]),
            ]);
            table.setColumnOrder(newLeafOrder);

            for (const itemId of newItems.visible) {
                const item = manageableItems.find((m) => m.id === itemId);
                item?.leafIds.forEach((leafId) => {
                    const col = leafColumns.find((c) => c.id === leafId);
                    if (col && !col.getIsVisible()) col.toggleVisibility(true);
                });
            }
            for (const itemId of newItems.hidden) {
                const item = manageableItems.find((m) => m.id === itemId);
                item?.leafIds.forEach((leafId) => {
                    const col = leafColumns.find((c) => c.id === leafId);
                    if (col && col.getIsVisible()) col.toggleVisibility(false);
                });
            }

            return newItems;
        },
        [leafColumns, getItems, table, manageableItems, expandItemsToLeafOrder],
    );

    const handleHideAllColumns = () => {
        updateItems((prev) => ({ visible: [prev.visible[0]], hidden: [...prev.visible.slice(1), ...prev.hidden] }));
    };

    const handleShowAllColumns = () => {
        updateItems((prev) => ({ visible: [...prev.visible, ...prev.hidden], hidden: [] }));
    };

    const handleToggleItemVisibility = (itemId: string) => {
        updateItems((prev) => {
            if (prev.visible.includes(itemId)) {
                return {
                    visible: prev.visible.filter((id) => id !== itemId),
                    hidden: [itemId, ...prev.hidden],
                };
            }
            if (prev.hidden.includes(itemId)) {
                return {
                    visible: [...prev.visible, itemId],
                    hidden: prev.hidden.filter((id) => id !== itemId),
                };
            }
            return prev;
        });
    };

    if (manageableItems.length === 0) {
        return null;
    }

    const items = getItems();

    return (
        <ListItem style={{ backgroundColor: colorSystem.neutral.white }}>
            <Flexbox
                gap={4}
                sx={{
                    flexDirection: 'column',
                }}
            >
                <MultiContainerDndContext items={items} updateItems={updateItems}>
                    <Flexbox
                        gap={8}
                        sx={{
                            flexDirection: 'column',
                        }}
                    >
                        <Flexbox
                            sx={{
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}
                        >
                            <Text variant={'h5'}>
                                <Trans>Shown in table</Trans>
                            </Text>
                            <Flexbox
                                sx={{
                                    flex: 1,
                                    paddingX: '12px',
                                }}
                            />
                            <TertiaryButton
                                size={'small'}
                                disabled={hasOnlyOneItemVisible || !enableColumnHiding}
                                onClick={handleHideAllColumns}
                            >
                                <Trans>Hide all</Trans>
                            </TertiaryButton>
                        </Flexbox>
                        <SortableContext items={items.visible}>
                            {items.visible.map((itemId) => {
                                const item = manageableItems.find((m) => m.id === itemId);
                                if (!item) return null;
                                return (
                                    <ColumnManagerItem
                                        key={itemId}
                                        itemId={itemId}
                                        label={item.label}
                                        isVisible={true}
                                        table={table}
                                        onClick={() => handleToggleItemVisibility(itemId as string)}
                                        disabled={hasOnlyOneItemVisible || !enableColumnHiding}
                                    />
                                );
                            })}
                        </SortableContext>
                        {!hasAllColumnVisible && shouldShowHidenColumnManager && (
                            <>
                                <Flexbox
                                    sx={{
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Text variant={'h5'}>
                                        <Trans>Hidden in table</Trans>
                                    </Text>
                                    <Flexbox
                                        sx={{
                                            flex: 1,
                                            paddingX: '12px',
                                        }}
                                    />
                                    <TertiaryButton
                                        size={'small'}
                                        disabled={hasAllColumnVisible || !enableColumnHiding}
                                        onClick={handleShowAllColumns}
                                    >
                                        <Trans>Show all</Trans>
                                    </TertiaryButton>
                                </Flexbox>

                                <SortableContext items={items.hidden}>
                                    {items.hidden.map((itemId) => {
                                        const item = manageableItems.find((m) => m.id === itemId);
                                        if (!item) return null;
                                        return (
                                            <ColumnManagerItem
                                                key={itemId}
                                                itemId={itemId}
                                                label={item.label}
                                                isVisible={false}
                                                table={table}
                                                onClick={() => handleToggleItemVisibility(itemId as string)}
                                                disabled={!enableColumnHiding}
                                            />
                                        );
                                    })}
                                </SortableContext>
                            </>
                        )}
                    </Flexbox>
                </MultiContainerDndContext>
            </Flexbox>
        </ListItem>
    );
}

function ColumnManagerItem<TData extends RowData>({
    itemId,
    label,
    isVisible,
    table,
    onClick,
    disabled,
}: {
    itemId: UniqueIdentifier;
    label: string;
    isVisible: boolean;
    table: Table<TData>;
    onClick: () => void;
    disabled?: boolean;
}) {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: itemId });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <div ref={setNodeRef} style={style}>
            <Flexbox
                gap={8}
                sx={{
                    justifyContent: 'space-between',
                }}
            >
                <Flexbox
                    gap={4}
                    sx={{
                        alignItems: 'center',
                        width: '100%',
                        minWidth: '250px',
                    }}
                >
                    {table.options.meta?.enableColumnOrdering && (
                        <TertiaryIconButton
                            size="small"
                            aria-label={t`Drag to reorder`}
                            disableRipple
                            ref={setNodeRef}
                            style={{ cursor: 'grab', color: colorSystem.neutral[5] }}
                            {...listeners}
                            {...attributes}
                            disabled={disabled}
                        >
                            <GripVerticalIcon />
                        </TertiaryIconButton>
                    )}

                    <Tooltip title={isVisible ? t`Hide column` : t`Show column`}>
                        <TertiaryButton size={'small'} onClick={onClick} style={{ width: '100%' }} disabled={disabled}>
                            <Flexbox
                                gap={24}
                                sx={{
                                    alignItems: 'center',
                                    width: '100%',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Text variant={'body-small'} showEllipsis={true} style={{ maxWidth: '200px' }}>
                                    {label}
                                </Text>
                                {isVisible ? <EyeIcon size="1em" /> : <EyeOffIcon size="1em" />}
                            </Flexbox>
                        </TertiaryButton>
                    </Tooltip>
                </Flexbox>
            </Flexbox>
        </div>
    );
}

export async function handleExportAsExcel<TData extends RowData>({ table }: { table: Table<TData> }): Promise<void> {
    const { default: writeXlsxFile } = await import('write-excel-file/browser');

    const cols = table
        .getAllLeafColumns()
        .filter((col) => col.getIsVisible())
        .filter((col) => col.columnDef.meta?.dataType !== 'generic');
    const rows = table.getRowModel().rows;

    const headerRow: Row = cols.map((col) => ({
        value: assertPresent(col.columnDef.meta).label(),
        fontWeight: 'bold' as const,
    }));

    const dataRows: Row[] = rows.map((row) =>
        cols.map((col) => {
            const meta = assertPresent(col.columnDef.meta);
            const value = row.getValue(col.id);

            if (!isPresent(value) || value === '') {
                return null;
            }

            switch (meta.dataType) {
                case 'text':
                    return {
                        type: String,
                        value: String(value),
                    };
                case 'number':
                    if (typeof value !== 'number' || isNaN(value) || !isFinite(value)) {
                        return null;
                    }

                    return {
                        type: Number,
                        value: Number(value),
                    };
                case 'enum':
                    return {
                        type: String,
                        value: getFilterConfig(col, 'enum')?.getOptionLabel(value) ?? JSON.stringify(value),
                    };
                case 'array':
                    return {
                        type: String,
                        value: (value as unknown[])
                            .map(
                                (v) =>
                                    getFilterConfig(col as Column<TData, unknown[]>, 'array')?.getOptionLabel(v) ??
                                    JSON.stringify(v),
                            )
                            .join(', '),
                    };
                case 'date':
                    return {
                        type: String,
                        value: formatToIso8601Date(value as string),
                    };
                case 'monetaryValue': {
                    // Export as numeric value with Excel currency format so it can be used in formulas
                    const mv = value as MonetaryValue;
                    const amount = Number(mv.amount);

                    if (isNaN(amount) || !isFinite(amount)) {
                        return null;
                    }

                    const columnRenderOptions = table.options.meta?.columnRenderOptions ?? {};
                    const renderOptionConfig = col.columnDef.meta?.renderOptionConfig;
                    const formatAs = columnRenderOptions[col.id] ?? renderOptionConfig?.default ?? 'default';

                    const currencyFormat = new Intl.NumberFormat(getLocale(), {
                        style: 'currency',
                        currency: mv.currency,
                        currencyDisplay: 'narrowSymbol',
                    });
                    const currencySymbol = displayCurrencySymbol(mv.currency, currencyFormat);

                    const decimals =
                        formattingOptions[formatAs as keyof typeof formattingOptions]?.minimumFractionDigits ?? 2;
                    const decimalPart = decimals > 0 ? '.' + '0'.repeat(decimals) : '';
                    const excelFormat = `"${currencySymbol}"#,##0${decimalPart}`;

                    return {
                        type: Number,
                        value: amount,
                        format: excelFormat,
                    };
                }

                case 'quantity': {
                    const qty = value as Quantity | null;
                    if (!qty) return null;
                    const numValue = QuantityHelper.from(qty).normalize().parseValue();
                    if (!Number.isFinite(numValue)) return null;
                    return { type: Number, value: numValue };
                }

                case 'availability': {
                    if (typeof value !== 'number' || isNaN(value) || !isFinite(value)) {
                        return null;
                    }
                    return { type: Number, value };
                }

                case 'generic':
                default:
                    throwErrorUnlessProduction(new Error('Error: Unsupported data type in exportAsExcel'), {
                        extra: { column: col.id, value: value },
                    });
                    return null;
            }
        }),
    );

    await writeXlsxFile([headerRow, ...dataRows], {
        columns: cols.map((col) => ({
            width: isPresent(col.columnDef.size) ? col.columnDef.size / 8 : undefined,
        })),
    }).toFile(`export-${formatToIso8601Date(new Date())}.xlsx`);
}

function ExportAsExcelMenuItem<TData extends RowData>({
    table,
    handleClose,
}: {
    table: Table<TData>;
    handleClose: () => void;
}) {
    const { enableExcelExport } = table.options.meta || {};

    if (!enableExcelExport) {
        return null;
    }

    return (
        <MenuItem
            label={t`Export as Excel`}
            onClick={async () => {
                await handleExportAsExcel({ table });
                handleClose();
            }}
            startIcon={<FileSpreadsheetIcon color={colorSystem.green[7]} />}
        />
    );
}

function EditColumnsMenuItem<TData extends RowData>({
    table,
    handleClose,
    onOpenDialog,
}: {
    table: Table<TData>;
    handleClose: () => void;
    onOpenDialog: () => void;
}) {
    const hasColumnManagerEnabled = table.options.meta?.enableColumnHiding || table.options.meta?.enableColumnOrdering;
    const hidableColumns = table.getAllLeafColumns().filter((col) => col.getCanHide());
    const shouldShowDialog = hidableColumns.length >= 12;

    if (!hasColumnManagerEnabled || !shouldShowDialog) {
        return null;
    }

    return (
        <MenuItem
            label={t`Manage columns`}
            startIcon={<SettingsIcon />}
            onClick={() => {
                onOpenDialog();
                handleClose();
            }}
        />
    );
}

function ResetTableMenuItem<TData extends RowData>({
    table,
    handleClose,
}: {
    table: Table<TData>;
    handleClose: () => void;
}) {
    const {
        columnFilters,
        columnOrder,
        columnPinning,
        columnVisibility,
        columnSizing,
        globalFilter,
        rowSelection,
        sorting,
    } = useSelector(table.store, ({ expanded, columnResizing, ...state }) => state, { compare: shallow });

    const columnRenderOptions = table.options.meta?.columnRenderOptions ?? {};

    // `stateSlicesEqual` ignores the prototype-less maps v9 writes, but stops at depth 3, short of a filter's value.
    const hasSameColumnFilters = isEqual(columnFilters, table.options.meta?.defaultInitialState?.columnFilters);
    const hasSameColumnOrder = stateSlicesEqual(columnOrder, table.options.meta?.defaultInitialState?.columnOrder);
    const hasSameColumnPinning = stateSlicesEqual(
        columnPinning,
        table.options.meta?.defaultInitialState?.columnPinning,
    );
    const hasSameColumnVisibility = stateSlicesEqual(
        columnVisibility,
        table.options.meta?.defaultInitialState?.columnVisibility,
    );
    const hasSameColumnSizing = stateSlicesEqual(columnSizing, table.options.meta?.defaultInitialState?.columnSizing);
    const hasSameGlobalFilter = isEqual(globalFilter, table.options.meta?.defaultInitialState?.globalFilter);
    const hasSameRowSelection = stateSlicesEqual(rowSelection, table.options.meta?.defaultInitialState?.rowSelection);
    const hasSameSorting = isEqual(sorting, table.options.meta?.defaultInitialState?.sorting);
    const hasSameRenderOptions = isEqual(columnRenderOptions, {});

    const disabledReset =
        hasSameColumnFilters &&
        hasSameColumnOrder &&
        hasSameColumnPinning &&
        hasSameColumnVisibility &&
        hasSameColumnSizing &&
        hasSameGlobalFilter &&
        hasSameRowSelection &&
        hasSameSorting &&
        hasSameRenderOptions;

    return (
        <MenuItem
            label={t`Reset table to default`}
            variant={'destructive'}
            startIcon={<UndoIcon />}
            onClick={() => {
                resetTable(table);
                handleClose();
            }}
            disabled={disabledReset}
        />
    );
}

export function VisibilityMenu<TData extends RowData>({ table }: { table: Table<TData> }): JSX.Element {
    const [anchorEl, setAnchorEl] = React.useState<Element | undefined>(undefined);
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const hasColumnManagerEnabled = table.options.meta?.enableColumnHiding || table.options.meta?.enableColumnOrdering;

    const handleOnClick = React.useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            if (isPresent(anchorEl)) {
                return;
            }
            setAnchorEl(e.currentTarget);
        },
        [anchorEl],
    );

    const handleClose = () => {
        setAnchorEl(undefined);
    };

    return (
        <>
            <SecondaryIconButton size={'medium'} onClick={handleOnClick} aria-label={t`Table settings`}>
                <EllipsisIcon />
            </SecondaryIconButton>

            <Menu anchorEl={anchorEl} open={isPresent(anchorEl)} onClose={handleClose} variant="menu">
                {hasColumnManagerEnabled && [<ColumnManager key="1" table={table} />, <Divider key="2" />]}
                <EditColumnsMenuItem table={table} handleClose={handleClose} onOpenDialog={() => setDialogOpen(true)} />
                <ExportAsExcelMenuItem table={table} handleClose={handleClose} />
                <ResetTableMenuItem table={table} handleClose={handleClose} />
            </Menu>
            <ColumnConfigDialog table={table} open={dialogOpen} onClose={() => setDialogOpen(false)} />
        </>
    );
}
